import React, { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { billingApi } from "./api";

const COLORS = {
  border: "rgba(255,255,255,0.07)",
  indigo: "#6366F1",
  violet: "#7C3AED",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  red: "#EF4444",
  green: "#10B981",
};

const appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#6366F1",
    colorBackground: "#0D1224",
    colorText: "#F8F7FF",
    colorDanger: "#EF4444",
    borderRadius: "10px",
  },
};

function PaymentForm({ plan, subscriptionId, onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError("");
    try {
      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (confirmError) {
        setError(confirmError.message || "Payment failed");
        setLoading(false);
        return;
      }
      const data = await billingApi.completeSubscription(subscriptionId);
      onSuccess(data.user);
    } catch (err) {
      setError(err.message || "Payment failed");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div
        style={{
          background: "rgba(255,255,255,0.03)",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {error && (
        <p style={{ color: COLORS.red, fontSize: 13, textAlign: "center", marginBottom: 12 }}>{error}</p>
      )}

      <button
        type="submit"
        disabled={!stripe || loading}
        style={{
          width: "100%",
          padding: "14px 20px",
          borderRadius: 10,
          border: "none",
          background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet})`,
          color: "#fff",
          fontSize: 15,
          fontWeight: 600,
          cursor: loading ? "wait" : "pointer",
          opacity: loading ? 0.7 : 1,
          marginBottom: 12,
        }}
      >
        {loading ? "Processing payment…" : `Pay $${(plan.priceCents / 100).toFixed(0)}${plan.period || "/mo"}`}
      </button>

      <button
        type="button"
        onClick={onClose}
        style={{
          display: "block",
          margin: "0 auto",
          background: "none",
          border: "none",
          color: COLORS.muted,
          fontSize: 14,
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </form>
  );
}

export default function PaymentModal({ planId, planMeta, onClose, onSuccess }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [publishableKey, setPublishableKey] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [plan, setPlan] = useState(planMeta);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await billingApi.subscribe(planId);
        if (cancelled) return;
        if (data.demo && data.user) {
          onSuccess(data.user);
          return;
        }
        setClientSecret(data.clientSecret);
        setPublishableKey(data.publishableKey);
        setSubscriptionId(data.subscriptionId);
        if (data.plan) setPlan(data.plan);
      } catch (err) {
        if (!cancelled) setError(err.message || "Could not start payment");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [planId, onSuccess]);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey, { locale: "en" }) : null),
    [publishableKey]
  );

  const elementsOptions = useMemo(
    () => ({
      clientSecret,
      appearance,
      locale: "en",
    }),
    [clientSecret]
  );

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        padding: "24px 16px",
        background: "rgba(8,12,24,0.9)",
        backdropFilter: "blur(12px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          minHeight: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#0D1224",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 20,
            padding: "28px 24px",
            width: "100%",
            maxWidth: 480,
            maxHeight: "calc(100vh - 48px)",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            margin: "auto",
            animation: "modalPop 0.35s ease",
            boxSizing: "border-box",
          }}
        >
        <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 22, marginBottom: 8, textAlign: "center" }}>
          Complete payment
        </h2>
        <p style={{ color: COLORS.muted, fontSize: 14, textAlign: "center", marginBottom: 24 }}>
          {plan?.name || planMeta?.name} plan — secure card payment via Stripe
        </p>

        {loading && <p style={{ color: COLORS.muted, textAlign: "center" }}>Preparing payment…</p>}

        {error && !loading && (
          <>
            <p style={{ color: COLORS.red, fontSize: 14, textAlign: "center", marginBottom: 16 }}>{error}</p>
            <button
              type="button"
              onClick={onClose}
              style={{
                display: "block",
                margin: "0 auto",
                background: "none",
                border: "none",
                color: COLORS.muted,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </>
        )}

        {!loading && !error && clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm
              plan={plan || planMeta}
              subscriptionId={subscriptionId}
              onSuccess={onSuccess}
              onClose={onClose}
            />
          </Elements>
        )}
        </div>
      </div>
    </div>
  );
}
